import { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { MenuTop } from '../../../components/menu/top/menu-top';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    IconButton,
    Typography,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TableSortLabel,
    TablePagination
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import '../styles/admin-page.css';
import '../styles/list-dd-curto.css';
import { deleteDDCurtoAnswer, getAllDDCurtoAnswer, IDDCurtoTestData } from '../../../features/search/service/search-service';
import { getCompaniesListSelect } from '../../../features/search/service/company-service';
import { SelectOption } from '../../../shared/types/select-options';
import { useTranslation } from '../../../shared/i18n/i18n-context';
import { ProjectLoader } from '../../../components/project-loader/project-loader';
import {
    AdminCrudAlerts,
    AdminCrudDeleteDialog,
    AdminCrudHeader,
    AdminCrudSearch
} from '../../../components/admin-crud/admin-crud';

type SortBy = 'nome' | 'record_id' | 'companyName' | 'createdAt' | 'totalTimeSeconds' | 'ed50' | 'k';
type SortDirection = 'asc' | 'desc';

const sortLabelKeys: Record<SortBy, string> = {
    nome: 'name',
    record_id: 'recordId',
    companyName: 'company',
    createdAt: 'createdAt',
    totalTimeSeconds: 'time',
    ed50: 'ED50',
    k: 'K'
};

const numericSortFields = new Set<SortBy>(['totalTimeSeconds', 'ed50', 'k']);
type SortValue = string | number | null;

const getSortValue = (test: IDDCurtoTestData, field: SortBy): SortValue => {
    if (field === 'createdAt') {
        const dateTime = test.createdAt ? new Date(test.createdAt).getTime() : NaN;
        return Number.isFinite(dateTime) ? dateTime : null;
    }

    if (numericSortFields.has(field)) {
        const value = field === 'totalTimeSeconds'
            ? Number(test.totalTimeSeconds)
            : Number.parseFloat(test[field] || '');

        return Number.isFinite(value) ? value : null;
    }

    const value = String(test[field] || '').trim().toLowerCase();
    return value || null;
};

const compareSortValues = (
    firstValue: SortValue,
    secondValue: SortValue,
    field: SortBy,
    direction: SortDirection,
    locale: string
) => {
    if (firstValue === null && secondValue === null) return 0;
    if (firstValue === null) return 1;
    if (secondValue === null) return -1;

    const comparison = numericSortFields.has(field) || field === 'createdAt'
        ? Number(firstValue) - Number(secondValue)
        : String(firstValue).localeCompare(String(secondValue), locale, { sensitivity: 'base' });

    return direction === 'asc' ? comparison : comparison * -1;
};

const formatDate = (dateString: string, locale: string, fallback: string) => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatTime = (seconds: number) => {
    if (!seconds) return '0m 0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
};

interface TestsTableProps {
    tests: IDDCurtoTestData[];
    sortBy: SortBy;
    sortDirection: SortDirection;
    onSort: (column: SortBy) => void;
    onDelete: (test: IDDCurtoTestData) => void;
}

const AdminTestsTable = memo(function AdminTestsTable({
    tests,
    sortBy,
    sortDirection,
    onSort,
    onDelete
}: TestsTableProps) {
    const { language, t } = useTranslation('admin');
    const locale = language === 'en' ? 'en-US' : 'pt-BR';

    const renderSortableHeader = (column: SortBy) => (
        <TableSortLabel
            active={sortBy === column}
            direction={sortBy === column ? sortDirection : 'asc'}
            onClick={() => onSort(column)}
            className="admin-tests-sort-label"
        >
            {t(sortLabelKeys[column])}
        </TableSortLabel>
    );

    return (
        <TableContainer 
            component={Paper} 
            className="admin-tests-table-container"
            sx={{ 
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto'
            }}
        >
            <Table size="small" stickyHeader className="admin-tests-table">
                <colgroup>
                    <col className="admin-tests-col-name" />
                    <col className="admin-tests-col-record" />
                    <col className="admin-tests-col-company" />
                    <col className="admin-tests-col-created" />
                    <col className="admin-tests-col-time" />
                    <col className="admin-tests-col-ed50" />
                    <col className="admin-tests-col-k" />
                    <col className="admin-tests-col-actions" />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('nome')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('record_id')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('companyName')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('createdAt')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('totalTimeSeconds')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('ed50')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('k')}
                        </TableCell>
                        <TableCell 
                            align="center"
                            className="admin-tests-table-cell-header"
                        >
                            {t('actions')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tests.map((test) => (
                        <TableRow 
                            key={test.id}
                            className="admin-tests-table-row"
                            hover
                        >
                            <TableCell className="admin-tests-table-cell-name" title={test.nome || t('notAvailable')}>
                                {test.nome || t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.record_id || t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-chip">
                                <Chip 
                                    label={test.companyName || t('notAvailable')}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: '#e8f5e9',
                                        color: '#2e7d32',
                                        height: '22px',
                                        fontSize: '11px',
                                        fontFamily: 'Roboto, sans-serif',
                                        fontWeight: 500
                                    }}
                                />
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {formatDate(test.createdAt || '', locale, t('notAvailable'))}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {formatTime(test.totalTimeSeconds || 0)}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.ed50 ? parseFloat(test.ed50).toFixed(2) : t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.k ? parseFloat(test.k).toFixed(4) : t('notAvailable')}
                            </TableCell>
                            <TableCell align="center" className="admin-tests-table-cell-actions">
                                <IconButton aria-label={t('confirmDelete')} color="error" onClick={() => onDelete(test)}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
});

export const AdminDDCurtoTestsPage = () => {
    const { language, t } = useTranslation('admin');
    const { t: menuT } = useTranslation('menu');
    const locale = language === 'en' ? 'en-US' : 'pt-BR';
    const [tests, setTests] = useState<IDDCurtoTestData[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTest, setSelectedTest] = useState<IDDCurtoTestData | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [companyFilter, setCompanyFilter] = useState<string>('');
    const [sortBy, setSortBy] = useState<SortBy>('createdAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const isSearchDeferred = searchTerm !== deferredSearchTerm;

    const fetchAllTests = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const [response, companiesResponse] = await Promise.all([
                getAllDDCurtoAnswer(),
                getCompaniesListSelect()
            ]);

            setTests(response.data);
            setCompanies(companiesResponse);
        } catch (error) {
            console.error('Erro ao carregar testes:', error);
            setError(t('testLoadError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const searchableTests = useMemo(() => {
        return tests.map((test) => ({
            test,
            searchableText: [
                test.nome,
                test.record_id,
                test.companyName
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
        }));
    }, [tests]);

    useEffect(() => {
        fetchAllTests();
    }, [fetchAllTests]);

    useEffect(() => {
        setPage(0);
    }, [companyFilter, deferredSearchTerm, sortBy, sortDirection]);

    useEffect(() => {
        if (loading) {
            return;
        }

        setFiltersLoading(true);
        const timeoutId = window.setTimeout(() => setFiltersLoading(false), 220);

        return () => window.clearTimeout(timeoutId);
    }, [companyFilter, deferredSearchTerm, loading, searchTerm, sortBy, sortDirection]);

    const filteredTests = useMemo(() => {
        const term = deferredSearchTerm.toLowerCase().trim();
        const filtered = searchableTests
            .filter(({ test, searchableText }) => {
            const matchesCompany = !companyFilter || test.companyId === companyFilter;

            if (!matchesCompany) {
                return false;
            }

            if (!term) {
                return true;
            }

            return searchableText.includes(term);
        })
            .map(({ test }) => test);

            const sorted = [...filtered].sort((firstTest, secondTest) => {
            const firstValue = getSortValue(firstTest, sortBy);
            const secondValue = getSortValue(secondTest, sortBy);
            return compareSortValues(firstValue, secondValue, sortBy, sortDirection, locale);
        });

        return sorted;
    }, [companyFilter, deferredSearchTerm, locale, searchableTests, sortBy, sortDirection]);

    const paginatedTests = useMemo(() => {
        const firstRowIndex = page * rowsPerPage;
        return filteredTests.slice(firstRowIndex, firstRowIndex + rowsPerPage);
    }, [filteredTests, page, rowsPerPage]);

    const handleChangePage = (_event: unknown, nextPage: number) => {
        setPage(nextPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextRowsPerPage = Number.parseInt(event.target.value, 10);

        setRowsPerPage(nextRowsPerPage);
        setPage(0);
    };

    const handleCompanyFilterChange = (event: SelectChangeEvent) => {
        setCompanyFilter(event.target.value);
    };

    const handleSort = useCallback((column: SortBy) => {
        if (sortBy === column) {
            setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
            return;
        }

        setSortBy(column);
        setSortDirection('asc');
    }, [sortBy]);

    const handleDeleteClick = useCallback((test: IDDCurtoTestData) => {
        setSelectedTest(test);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = async () => {
        if (!selectedTest) return;

        try {
            setError('');
            await deleteDDCurtoAnswer(selectedTest.id);
            setSuccessMessage(t('testDeletedSuccess').replace('{name}', selectedTest.nome || t('notAvailable')));
            setDeleteDialogOpen(false);
            setSelectedTest(null);
            
            fetchAllTests();
            
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Erro ao excluir teste:', error);
            setError(t('testDeleteError'));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedTest(null);
    };

    const hasActiveFilters = Boolean(deferredSearchTerm.trim() || companyFilter);
    const showLoadingOverlay = loading || filtersLoading || isSearchDeferred;

    return (
        <div>
            <MenuTop />

            <div className="admin-tests-container">
                <AdminCrudHeader
                    title={t('appliedTests')}
                    subtitle={`${filteredTests.length} ${filteredTests.length === 1 ? t('testSingular') : t('testPlural')} ${filteredTests.length === 1 ? t('foundSingular') : t('foundPlural')}`}
                    breadcrumbItems={[
                        { label: menuT('home'), to: '/home' },
                        { label: t('causalePanel'), to: '/admin-painel-causale' },
                        { label: t('appliedTests') }
                    ]}
                />

                <div className="admin-tests-filters-container">
                        <AdminCrudSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder={t('searchTests')}
                        />
                        <FormControl className="admin-tests-filter-field admin-tests-company-filter" size="small">
                            <InputLabel id="admin-tests-company-filter-label">{t('company')}</InputLabel>
                            <Select
                                labelId="admin-tests-company-filter-label"
                                value={companyFilter}
                                label={t('company')}
                                onChange={handleCompanyFilterChange}
                            >
                                <MenuItem value="">{t('allCompanies')}</MenuItem>
                                {companies.map((company) => (
                                    <MenuItem key={company.id} value={String(company.id)}>
                                        {company.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </div>

                <AdminCrudAlerts
                    error={error}
                    success={successMessage}
                    onClearError={() => setError('')}
                    onClearSuccess={() => setSuccessMessage('')}
                />

                <div className="admin-tests-results">
                    {showLoadingOverlay && (
                        <div className="admin-tests-loading-overlay">
                            <div className="admin-tests-loading-panel">
                                <ProjectLoader label={t('loadingTests')} compact />
                            </div>
                        </div>
                    )}

                    {!loading && (
                    <>
                        {hasActiveFilters && filteredTests.length === 0 && (
                            <Alert severity="info" className="admin-tests-alert">
                                {t('noTestsForFilters')}
                            </Alert>
                        )}

                        {!hasActiveFilters && filteredTests.length === 0 && (
                            <div className="admin-tests-empty-state">
                                <HourglassEmptyIcon className="admin-tests-empty-state-icon" />
                                <Typography className="admin-tests-empty-state-text">
                                    {t('noTestsFound')}
                                </Typography>
                                <Typography className="admin-tests-empty-state-subtext">
                                    {t('testsWillAppear')}
                                </Typography>
                            </div>
                        )}

                        {filteredTests.length > 0 && (
                            <>
                                <AdminTestsTable
                                    tests={paginatedTests}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    onDelete={handleDeleteClick}
                                />
                                <TablePagination
                                    component="div"
                                    className="admin-tests-pagination"
                                    count={filteredTests.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    labelRowsPerPage={t('rowsPerPage')}
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${language === 'en' ? 'of' : 'de'} ${count}`}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}
                    </>
                    )}
                </div>
            </div>

            <AdminCrudDeleteDialog
                open={deleteDialogOpen}
                title={t('confirmDelete')}
                message={t('confirmDeleteTestQuestion')}
                warning={t('irreversibleAction')}
                cancelLabel={t('cancel')}
                confirmLabel={t('delete')}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            >
                {selectedTest && (
                    <div className="admin-tests-dialog-info">
                        <Typography variant="body2" className="admin-tests-dialog-info-text">
                            <strong>{t('name')}:</strong> {selectedTest.nome}
                        </Typography>
                        <Typography variant="body2" className="admin-tests-dialog-info-text">
                            <strong>{t('recordId')}:</strong> {selectedTest.record_id}
                        </Typography>
                        <Typography variant="body2" className="admin-tests-dialog-info-text">
                            <strong>{t('company')}:</strong> {selectedTest.companyName}
                        </Typography>
                        <Typography variant="body2" className="admin-tests-dialog-info-text">
                            <strong>{t('createdAt')}:</strong> {formatDate(selectedTest.createdAt || '', locale, t('notAvailable'))}
                        </Typography>
                    </div>
                )}
            </AdminCrudDeleteDialog>
        </div>
    );
};
